import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { SEEDED_PERSONAS, type SeededPersona } from '@aurora-pvi/testing';
import { canReach } from '@aurora-pvi/contracts';
import { setApiIdentity } from '../../api/apiFetch';
import {
  createHttpAuthRepository,
  type AuthConfig,
  type SessionInfo,
} from '../../api/httpAuthRepository';

/**
 * Session and span of control.
 *
 * There is a real session underneath this now. Identity used to live in
 * `localStorage` and travel as a header; it is an httpOnly cookie the server
 * issued, backed by a row in `iam.session` with an idle expiry that slides and
 * an absolute one that does not. This provider's job is to find out who that
 * cookie says you are, and — in a development environment — to establish one
 * for whichever persona you pick.
 *
 * The persona switcher is not a convenience. It is the fastest way to prove the
 * authorisation model works: switching from the enterprise executive to a
 * product owner must visibly change the numbers on every screen, because the
 * narrowing happens server-side rather than by hiding rows in the browser. So
 * it stays — and it now signs in for real, through the same session machinery a
 * federated login uses, with one step replaced. Where an identity provider is
 * configured the server refuses that endpoint outright, so the switcher is
 * absent rather than merely hidden.
 *
 * `allowedModules` drives navigation. A module the user cannot reach is absent
 * from the rail entirely — but the route is still guarded, because hiding a menu
 * item is presentation, not security.
 */

interface SessionValue {
  currentUser: SeededPersona;
  availableUsers: SeededPersona[];
  switchUser: (userId: string) => void;
  allowedModules: string[];
  can: (module: string) => boolean;
  /**
   * Permission codes this person holds, straight from `iam.role_permission`.
   *
   * Empty until the first `/me` returns. `canOpen` treats that as "not yet
   * known" rather than "denied", so the rail does not flicker every page into
   * existence on load.
   */
  permissions: string[];
  permissionsLoaded: boolean;
  /** Whether this person may open a `/v2/...` route. */
  canOpen: (path: string) => boolean;
  /** What kind of front door this deployment has. Null until the API answers. */
  authConfig: AuthConfig | null;
  /** The live session, or null where nobody is signed in. */
  session: SessionInfo | null;
  /** Where to send somebody who needs to sign in. Null in a development build. */
  signInUrl: string | null;
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Where the chosen identity is remembered between page loads. */
const IDENTITY_KEY = 'aurora.pvi.identity.v1';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * A signed-in person the fixture repositories can still be handed.
 *
 * `createFixtureRepositories` wants a `SeededPersona`, and in a deployment with a
 * real provider the person signing in is not one of the seeded personas. Where
 * the API is wired nothing reads these fields — every screen's numbers come
 * from the server — so the honest thing is to carry across what the session
 * actually said and leave the fixture-only fields empty rather than invent a
 * scope code that would silently narrow a fixture nobody is reading.
 */
function personaFor(session: SessionInfo): SeededPersona {
  const matched = SEEDED_PERSONAS.find((user) => user.id === session.personId);
  if (matched) return matched;
  return {
    id: session.personId,
    displayName: session.displayName,
    roleLabel: session.roleLabel,
    scopeLabel: session.scopeLabel,
    personaId: 'federated',
    scopeId: '',
    scopeCode: '',
    scopeType: (session.scopeType as SeededPersona['scopeType']) ?? 'Enterprise',
    allowedModules: [],
    canSeeRestrictedFinance: session.permissions.includes('view_finance'),
    canSeeWorkforce: session.permissions.includes('view_workforce'),
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useMemo(
    () => (API_BASE_URL ? createHttpAuthRepository(API_BASE_URL) : null),
    [],
  );

  /*
    The chosen persona survives a reload — in a development build.

    It used to be the whole identity mechanism. It is now only a note of which
    persona to re-establish a session for, and the session is what actually
    authorises: a pasted link or a refresh silently putting the reader back as
    the CIO would be as misleading as it ever was, but the fix is now a sign-in
    rather than a remembered string.
  */
  const [preferredPersona, setPreferredPersona] = useState<string>(() => {
    const first = SEEDED_PERSONAS[0];
    if (!first) throw new Error('No demo users are defined in @aurora-pvi/testing.');
    if (typeof window === 'undefined') return first.id;
    const stored = window.localStorage.getItem(IDENTITY_KEY);
    return stored && SEEDED_PERSONAS.some((user) => user.id === stored) ? stored : first.id;
  });

  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  /*
    Establish a session, and in a development build establish one automatically.

    Automatically, because a checkout that opens on a sign-in page every time is
    a checkout nobody demos; and honestly, because what it establishes is a real
    session with a real expiry rather than a remembered string. Where a provider
    is configured this does nothing: the person signs in through it, and until
    they do the screens are refused by the server rather than filled in
    optimistically here.
  */
  useEffect(() => {
    if (!auth) return undefined;
    let cancelled = false;

    void (async () => {
      const config = await auth.config();
      if (cancelled) return;
      setAuthConfig(config);

      let current = await auth.session();
      if (!current && config?.canSwitchIdentity) {
        if (await auth.signInAs(preferredPersona)) current = await auth.session();
      }
      if (cancelled) return;
      setSession(current);
      setPermissionsLoaded(current !== null);
    })();

    return () => {
      cancelled = true;
    };
  }, [auth, preferredPersona]);

  const currentUser = useMemo(() => {
    if (session) return personaFor(session);
    const fallback = SEEDED_PERSONAS.find((user) => user.id === preferredPersona) ?? SEEDED_PERSONAS[0];
    if (!fallback) throw new Error('No demo users are defined in @aurora-pvi/testing.');
    return fallback;
  }, [session, preferredPersona]);

  /*
    The development fallback header, set synchronously during render.

    An effect runs after the first paint, and react-query would already have
    fired its requests with nothing attached. The server prefers the session
    cookie and ignores this entirely where a provider is configured; it exists
    so the first few requests of a fresh checkout are answered rather than
    refused while the demo sign-in is still in flight.
  */
  setApiIdentity(currentUser.id);

  const switchUser = useCallback(
    (userId: string) => {
      setPreferredPersona(userId);
      if (typeof window !== 'undefined') window.localStorage.setItem(IDENTITY_KEY, userId);
      if (!auth) return;
      // A switch is a sign-in. The previous session is replaced by the cookie
      // the server sets, so two personas cannot be live in one browser at once.
      void (async () => {
        if (await auth.signInAs(userId)) setSession(await auth.session());
      })();
    },
    [auth],
  );

  const signOut = useCallback(() => {
    if (!auth) return;
    void (async () => {
      await auth.signOut();
      setSession(null);
      setPermissionsLoaded(false);
    })();
  }, [auth]);

  /*
    Permissions come from the session, not from the fixture beside this file.

    `SeededPersona.allowedModules` is a hand-kept list and drifted the moment a role
    changed — the CIO kept reaching the governance queue after `administer` was
    taken off the role, because the list still said they could. Asking the
    server means granting a permission is the whole change.
  */
  const permissions = useMemo(() => session?.permissions ?? [], [session]);

  const canOpen = useCallback(
    (path: string) => (permissionsLoaded ? canReach(path, permissions) : true),
    [permissions, permissionsLoaded],
  );

  const can = useCallback(
    (module: string) => currentUser.allowedModules.includes(module),
    [currentUser],
  );

  const signInUrl = useMemo(
    () =>
      auth && authConfig?.mode === 'oidc'
        ? auth.loginUrl(
            typeof window === 'undefined'
              ? '/v2/executive'
              : `${window.location.pathname}${window.location.search}`,
          )
        : null,
    [auth, authConfig],
  );

  const value = useMemo<SessionValue>(
    () => ({
      currentUser,
      availableUsers: SEEDED_PERSONAS,
      switchUser,
      allowedModules: currentUser.allowedModules,
      can,
      permissions,
      permissionsLoaded,
      canOpen,
      authConfig,
      session,
      signInUrl,
      signOut,
    }),
    [
      currentUser,
      switchUser,
      can,
      permissions,
      permissionsLoaded,
      canOpen,
      authConfig,
      session,
      signInUrl,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a <SessionProvider>.');
  }
  return context;
}
